import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import * as turf from "@turf/turf";
import { useTheme } from "next-themes";
import { mapStyleFor } from "@/lib/mapStyle";
import { RouteSvgOverlay } from "@/components/RouteSvgOverlay";

// Static map that renders a saved run route as an SVG overlay on top of a keyless
// MapLibre + CARTO base map (theme-aware: dark in dark mode, light in light mode).
// The route line + start/end markers are drawn with SVG (projected via map.project on
// render/move) instead of GL line/circle layers.
export const SavedRunMap = ({ routeGeojson, height = "16rem" }) => {
  const container = useRef(null);
  const map = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const { resolvedTheme } = useTheme();

  const coords = (routeGeojson && Array.isArray(routeGeojson.coordinates)) ? routeGeojson.coordinates : [];

  useEffect(() => {
    if (!coords.length) return undefined;
    if (map.current) return undefined;

    let bounds;
    try { bounds = turf.bbox(routeGeojson); } catch (e) { bounds = null; }

    map.current = new maplibregl.Map({
      container: container.current,
      style: mapStyleFor(resolvedTheme),
      center: coords[0],
      zoom: 14,
      attributionControl: { compact: true },
    });
    // Diagnostic listeners — surface tile/style/worker + WebGL context issues (esp. in production)
    map.current.on("error", (e) => console.error("MAP ERROR", e));
    map.current.on("webglcontextlost", (e) => console.warn("WEBGL LOST", e));
    map.current.on("webglcontextrestored", () => console.log("WEBGL RESTORED"));

    if (bounds) map.current.fitBounds(bounds, { padding: 40, duration: 0 });
    setMapInstance(map.current);

    return () => { if (map.current) { map.current.remove(); map.current = null; } setMapInstance(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeGeojson]);

  // Swap the basemap if the user toggles theme while viewing the run (camera is preserved).
  useEffect(() => {
    if (map.current) map.current.setStyle(mapStyleFor(resolvedTheme));
  }, [resolvedTheme]);

  return (
    <div
      style={{ position: "relative", height, width: "100%" }}
      className="rounded-2xl overflow-hidden border border-border"
      data-testid="saved-run-map"
    >
      <div ref={container} style={{ position: "absolute", inset: 0 }} />
      <RouteSvgOverlay map={mapInstance} coordinates={coords} showEndpoints lineWidth={4} />
    </div>
  );
};
